import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import quickBooksInvoicesApi from "../services/quickBooksInvoicesApi";
import {
  QuickBooksInvoiceApiError,
  QuickBooksInvoiceLatest,
  QuickBooksInvoiceRequest,
  isActiveInvoiceStatus,
} from "../types/quickBooksInvoices";

const DEFAULT_POLL_DELAYS_MS = [1500, 3000, 6000, 12000, 30000];
const MAX_STATUS_FAILURES = 5;

export interface QuickBooksInvoiceStatusHookOptions {
  enabled?: boolean;
  pollDelaysMs?: number[];
  onCompleted?: (request: QuickBooksInvoiceRequest, context: { live: boolean }) => void;
  api?: Pick<typeof quickBooksInvoicesApi, "getLatestForShipment" | "getStatus" | "retry"> &
    Partial<Pick<typeof quickBooksInvoicesApi, "replace">>;
}

export interface QuickBooksInvoiceStatusHookResult {
  status: QuickBooksInvoiceLatest;
  loading: boolean;
  polling: boolean;
  retrying: boolean;
  replacing?: boolean;
  error: QuickBooksInvoiceApiError | null;
  refresh: () => void;
  retry: () => Promise<QuickBooksInvoiceRequest | null>;
  replace?: () => Promise<QuickBooksInvoiceRequest | null>;
}

export function useQuickBooksInvoiceStatus(
  shipmentId: number | null | undefined,
  options: QuickBooksInvoiceStatusHookOptions = {},
): QuickBooksInvoiceStatusHookResult {
  const api = options.api || quickBooksInvoicesApi;
  const delays = useMemo(
    () => options.pollDelaysMs?.length && options.pollDelaysMs.every((delay) => Number.isFinite(delay) && delay > 0)
      ? options.pollDelaysMs : DEFAULT_POLL_DELAYS_MS,
    [options.pollDelaysMs],
  );
  const [status, setStatus] = useState<QuickBooksInvoiceLatest>(null);
  const [loading, setLoading] = useState(Boolean(shipmentId));
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<QuickBooksInvoiceApiError | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const generation = useRef(0);
  const lastCompletion = useRef<string | null>(null);
  const liveRequest = useRef<string | null>(null);
  const previousShipment = useRef<number | null | undefined>(undefined);
  const callbackRef = useRef(options.onCompleted);
  const retryController = useRef<AbortController | null>(null);
  const retryGenerationRef = useRef<number | null>(null);
  const replaceController = useRef<AbortController | null>(null);
  const replaceGenerationRef = useRef<number | null>(null);
  callbackRef.current = options.onCompleted;

  const refresh = useCallback(() => setRefreshKey((value) => value + 1), []);

  useEffect(() => {
    const currentGeneration = ++generation.current;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    let delayIndex = 0;
    let previousStatus: string | undefined;
    let consecutiveFailures = 0;

    const shipmentChanged = previousShipment.current !== shipmentId;
    previousShipment.current = shipmentId;
    if (shipmentChanged) setStatus(null);
    setError(null);
    setLoading(Boolean(shipmentId));
    setPolling(false);
    if (shipmentChanged) {
      lastCompletion.current = null;
      liveRequest.current = null;
    }

    if (!shipmentId || options.enabled === false) {
      setLoading(false);
      return () => controller.abort();
    }

    const current = () => !stopped && generation.current === currentGeneration;
    const recoverRead = (nextError: QuickBooksInvoiceApiError, again: () => void) => {
      if (!current() || nextError.isCanceled) return;
      setLoading(false);
      setError(nextError);
      const transient = nextError.status !== 401 && nextError.status !== 403 &&
        (nextError.isNetworkError || nextError.status === 429 || (nextError.status !== undefined && nextError.status >= 500 && nextError.status <= 599));
      if (!transient || ++consecutiveFailures >= MAX_STATUS_FAILURES) {
        setPolling(false);
        return;
      }
      setPolling(true);
      const serverWait = Number.isFinite(nextError.retryAfter) ? Math.max(0, nextError.retryAfter!) * 1000 : 0;
      const wait = Math.min(2147483647, Math.max(serverWait, delays[Math.min(delayIndex++, delays.length - 1)]));
      timer = setTimeout(() => { if (current()) again(); }, wait);
    };
    const applyStatus = (next: QuickBooksInvoiceRequest | null) => {
      if (!current()) return;
      setStatus(next);
      if (next && isActiveInvoiceStatus(next.status)) liveRequest.current = next.request_id;
      if (next?.status === "succeeded" && lastCompletion.current !== next.request_id) {
        lastCompletion.current = next.request_id;
        // Hydrate historical success too, but only a witnessed transition merits a toast.
        callbackRef.current?.(next, { live: liveRequest.current === next.request_id });
      }
    };

    const poll = async (requestId: string): Promise<void> => {
      if (!current()) return;
      setPolling(true);
      try {
        const next = await api.getStatus(requestId, controller.signal);
        if (!current()) return;
        setError(null);
        consecutiveFailures = 0;
        applyStatus(next);
        if (next.status !== previousStatus) delayIndex = 0;
        previousStatus = next.status;
        if (isActiveInvoiceStatus(next.status)) {
          timer = setTimeout(() => void poll(requestId), delays[Math.min(delayIndex++, delays.length - 1)]);
        } else {
          setPolling(false);
        }
      } catch (caught) {
        recoverRead(caught as QuickBooksInvoiceApiError, () => void poll(requestId));
      }
    };

    const loadLatest = async () => {
      try {
        const latest = await api.getLatestForShipment(shipmentId, controller.signal);
        if (!current()) return;
        setLoading(false);
        setError(null);
        consecutiveFailures = 0;
        applyStatus(latest);
        if (latest && isActiveInvoiceStatus(latest.status)) void poll(latest.request_id);
        else setPolling(false);
      } catch (caught) {
        recoverRead(caught as QuickBooksInvoiceApiError, () => void loadLatest());
      }
    };
    void loadLatest();

    return () => {
      stopped = true;
      controller.abort();
      if (timer) clearTimeout(timer);
      if (retryGenerationRef.current === currentGeneration) {
        retryController.current?.abort();
        retryController.current = null;
        retryGenerationRef.current = null;
        setRetrying(false);
      }
      if (replaceGenerationRef.current === currentGeneration) {
        replaceController.current?.abort();
        replaceController.current = null;
        replaceGenerationRef.current = null;
        setReplacing(false);
      }
    };
  }, [api, delays, options.enabled, refreshKey, shipmentId]);

  const retry = useCallback(async () => {
    if (!status?.request_id || !status.can_retry) return null;
    // React state is asynchronous: protect two clicks in the same render too.
    if (retryController.current || retrying) return null;
    const controller = new AbortController();
    retryController.current = controller;
    const retryGeneration = generation.current;
    retryGenerationRef.current = retryGeneration;
    setRetrying(true);
    try {
      const next = await api.retry(status.request_id, controller.signal);
      if (generation.current !== retryGeneration) return null;
      liveRequest.current = next.request_id;
      setStatus(next);
      setError(null);
      refresh();
      return next;
    } catch (caught) {
      const nextError = caught as QuickBooksInvoiceApiError;
      if (!nextError.isCanceled && generation.current === retryGeneration) setError(nextError);
      return null;
    } finally {
      if (generation.current === retryGeneration) setRetrying(false);
      if (retryController.current === controller) retryController.current = null;
    }
  }, [api, refresh, retrying, status]);

  const replace = useCallback(async () => {
    if (!status?.request_id || !status.can_replace || !api.replace) return null;
    if (replaceController.current || replacing || retryController.current) return null;
    const controller = new AbortController();
    replaceController.current = controller;
    const replaceGeneration = generation.current;
    replaceGenerationRef.current = replaceGeneration;
    setReplacing(true);
    try {
      const next = await api.replace(status.request_id, controller.signal);
      if (generation.current !== replaceGeneration) return null;
      liveRequest.current = next.request_id;
      setStatus(next);
      setError(null);
      refresh();
      return next;
    } catch (caught) {
      const nextError = caught as QuickBooksInvoiceApiError;
      if (!nextError.isCanceled && generation.current === replaceGeneration) setError(nextError);
      return null;
    } finally {
      if (generation.current === replaceGeneration) setReplacing(false);
      if (replaceController.current === controller) replaceController.current = null;
    }
  }, [api, refresh, replacing, status]);

  return { status, loading, polling, retrying, replacing, error, refresh, retry, replace };
}

export default useQuickBooksInvoiceStatus;
